import {Filter, FilterDocumentType} from '@/types/filter';
import i18n from '@/i18n/i18n';
import {format, isToday, isTomorrow} from 'date-fns';
import {SupportedLanguage} from '@/types/common';
import {enUS, de} from 'date-fns/locale';
import {store} from '@/redux-store/store';
import {AGE_GROUPS} from '@/constants';

const weekdayLabels = [
  'weekdaySunday',
  'weekdayMonday',
  'weekdayTuesday',
  'weekdayWednesday',
  'weekdayThursday',
  'weekdayFriday',
  'weekdaySaturday',
  'weekdaySunday'
];

const ageGroupMap = new Map(
  AGE_GROUPS.map((group) => [group.value, group.labelKey])
);

const {myGroups} = store.getState().groups;

let currentCompanies = store.getState().userInfos.userInfos.companies;

let currentSports = store.getState().sports.sports;

// Without subscribing we sometimes get empty arrays from the store

const unsubscribeSports = store.subscribe(() => {
  const newSports = store.getState().sports.sports;
  if (newSports !== currentSports) {
    currentSports = newSports;
  }
});

const usubscribeCompanies = store.subscribe(() => {
  const newCompanies = store.getState().userInfos.userInfos.companies;
  if (newCompanies !== currentCompanies) {
    currentCompanies = newCompanies;
  }
});

const localeMap: {[key in SupportedLanguage]: Locale} = {
  en: enUS,
  de: de
};

const formatDate = (date: Date | null, localeKey: SupportedLanguage) => {
  const locale = localeMap[localeKey];

  if (date) {
    if (isToday(date)) {
      return i18n.t('filterToday');
    } else if (isTomorrow(date)) {
      return i18n.t('filterTomorrow');
    } else {
      return format(date, 'P', {locale});
    }
  }
  return '';
};

const formatTime = (time: string | null | undefined) => {
  if (time) {
    return time.slice(0, 5);
  }
  return '';
};

const getWeekdaysLabels = (labels: string[]) => {
  const translatedLabels: string[] = [];
  labels.forEach((label) => {
    if (label !== undefined) {
      const labelToTranslate = label + '_short';
      const translatedLabel = i18n.t(labelToTranslate);
      translatedLabels.push(translatedLabel);
    }
  });
  return translatedLabels;
};

const getAgeGroups = (ageGroups: string[]) => {
  const translatedAgeGroups: string[] = [];
  ageGroups.forEach((group) => {
    const labelKey = ageGroupMap.get(group);
    if (labelKey) {
      const translatedLabel = i18n.t(labelKey);
      translatedAgeGroups.push(translatedLabel);
    }
  });
  return translatedAgeGroups;
};

const getActiveFilters = (
  currentFilters: Filter,
  initialFilters: Filter,
  searchType: FilterDocumentType
) => {
  const userId = store.getState().userInfos.userInfos._id;
  const currentLocale: SupportedLanguage = i18n.language as SupportedLanguage;
  const activeFilters: {label: string; value: string}[] = [];

  if (
    currentFilters.sports &&
    currentFilters.sports.length > 0 &&
    Object.keys(currentSports).length > 0
  ) {
    const sportLabels: string[] = [];
    currentFilters.sports.forEach((sport) => {
      const currentSport = currentSports[sport];
      const sportLabel =
        i18n.language === 'en'
          ? currentSport?.labels.en
          : currentSport?.labels.de;
      if (sportLabel !== '') {
        sportLabels.push(sportLabel);
      }
    });

    let labels: string;
    if (sportLabels.length > 5) {
      labels = sportLabels.slice(0, 5).join(', ') + '...';
    } else {
      labels = sportLabels.join(', ');
    }

    activeFilters.push({
      label: labels,
      value: 'sports'
    });
    unsubscribeSports();
  }
  if (currentFilters.location?.coordinates && currentFilters.locationLabel) {
    activeFilters.push({
      label: currentFilters.locationLabel,
      value: 'location'
    });
  }

  if (
    currentFilters.isPaid !== initialFilters.isPaid &&
    searchType === 'event'
  ) {
    activeFilters.push({
      label: currentFilters.isPaid
        ? i18n.t('paid_events')
        : i18n.t('free_events'),
      value: 'isPaid'
    });
  }

  if (
    currentFilters.online !== initialFilters.online &&
    searchType === 'event'
  ) {
    activeFilters.push({
      label: currentFilters.online ? i18n.t('online') : i18n.t('offline'),
      value: 'online'
    });
  }

  if (
    currentFilters.ageGroups &&
    currentFilters.ageGroups.length > 0 &&
    !currentFilters.ageGroups.includes('ALL') &&
    (searchType === 'event' || searchType === 'onDemandEvent')
  ) {
    const ageGroups = getAgeGroups(currentFilters.ageGroups);
    if (ageGroups) {
      activeFilters.push({
        label: ageGroups.join(', '),
        value: 'ageGroups'
      });
    }
  }

  if (
    currentFilters.group &&
    currentFilters.group !== initialFilters.group &&
    searchType === 'event'
  ) {
    const groupLabel = myGroups.find(
      (g) => g._id === currentFilters.group
    )?.title;
    activeFilters.push({
      label: groupLabel || 'Team',
      value: 'group'
    });
  }

  if (
    currentFilters.start &&
    currentFilters.start.toString() !== initialFilters.start?.toString() &&
    searchType === 'event'
  ) {
    activeFilters.push({
      label: `${i18n.t('time_from')} ${formatDate(
        currentFilters.start || null,
        currentLocale
      )}`,
      value: 'start'
    });
  }

  if (
    currentFilters.startTime &&
    currentFilters.startTime !== initialFilters.startTime &&
    searchType === 'event'
  ) {
    activeFilters.push({
      label: `${i18n.t('time_from')} ${formatTime(currentFilters.startTime)}`,
      value: 'startTime'
    });
  }

  if (
    currentFilters.endTime &&
    currentFilters.endTime !== initialFilters.endTime &&
    searchType === 'event'
  ) {
    activeFilters.push({
      label: `${i18n.t('time_upTo')} ${formatTime(currentFilters.endTime)}`,
      value: 'endTime'
    });
  }

  if (
    currentFilters.companies &&
    currentFilters.companies.length > 0 &&
    currentCompanies
  ) {
    const companyLabels: string[] = [];

    currentFilters.companies.forEach((company) => {
      if (currentCompanies) {
        const label = currentCompanies.find((c) => c.id._id === company)?.id
          .title;
        if (label) {
          companyLabels.push(label);
        }
      }
    });

    let labels: string;
    if (companyLabels.length > 5) {
      labels = companyLabels.slice(0, 5).join(', ') + '...';
    } else {
      labels = companyLabels.join(', ');
    }

    activeFilters.push({
      label: labels,
      value: 'companies'
    });
    usubscribeCompanies();
  }

  if (
    currentFilters.weekdays &&
    currentFilters.weekdays.length < 7 &&
    searchType === 'event'
  ) {
    const stringLabels: string[] = [];
    currentFilters.weekdays.forEach((weekday) => {
      const stringLabel = weekdayLabels[weekday];
      stringLabels.push(stringLabel);
    });

    const weekdaysLabels = getWeekdaysLabels(stringLabels);

    if (weekdayLabels) {
      activeFilters.push({
        label: weekdaysLabels.join(', '),
        value: 'weekdays'
      });
    }
  }

  if (searchType === 'event') {
    if (currentFilters.pastEvents) {
      activeFilters.push({
        label: i18n.t('my_past_events'),
        value: 'pastEvents'
      });
    } else if (
      currentFilters.participants &&
      userId &&
      currentFilters.participants.includes(userId)
    ) {
      activeFilters.push({
        label: i18n.t('my_events'),
        value: 'participants'
      });
    }
  }

  if (searchType === 'group' && currentFilters.myTeams) {
    activeFilters.push({
      label: i18n.t('my_groups'),
      value: 'myTeams'
    });
  }

  return activeFilters;
};

export default getActiveFilters;
